import React from 'react';
import { Battery, Zap, Clock, Shield, Maximize2, TrendingUp } from 'lucide-react';
import { DraggableElement } from '../../DraggableElement';
import { EnPowerWordmarkInline } from '../../EnPowerLogo';

const UNIT_IMG = '/speicher-unit2.jpeg';
const BESS_IMG = '/bess-grossspeicher.png';
export const SOLAREA_LOGO_SRC = '/solarea-logo-rgb.svg';
export const BESS_PARTNER_LABEL = 'EN:POWER und Solarea';

export const parseGermanNumber = (value) => {
  const normalized = String(value || '').replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

export const formatGermanNumber = (value, maximumFractionDigits = 2) => new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits,
}).format(value);

export const isBessOffer = (data = {}) => {
  const marker = `${data.offerVariant || ''} ${data.angebotstyp || ''} ${data.offerTypeLabel || ''} ${data.projektpartner || ''}`.toLowerCase();
  return marker.includes('bess') || marker.includes('großspeicher') || marker.includes('grossspeicher');
};

export const getBessExtensionPayment = (data = {}) => {
  const fromBackend = String(data.verlaengerungszahlung || data.verlaengerung_zahlung || '').trim();
  if (fromBackend.includes('€')) {
    return fromBackend;
  }

  const perMwRaw = data.verlaengerungszahlung_pro_mw || data.extensionPayment || data.bessExtensionPayment || fromBackend;
  const perMw = parseGermanNumber(perMwRaw);

  if (!(perMw > 0)) {
    return '';
  }

  const bessMw = parseGermanNumber(data.leistung_mw)
    || (parseGermanNumber(data.leistung_kw) > 0 ? parseGermanNumber(data.leistung_kw) / 1000 : 0);
  const totalPerYear = bessMw > 0 ? perMw * bessMw : parseGermanNumber(data.verlaengerungszahlung_gesamt_jahr);

  if (!(totalPerYear > 0)) {
    return `${formatGermanNumber(perMw, 0)} € pro MW/Jahr`;
  }

  return `${formatGermanNumber(perMw, 0)} € pro MW/Jahr = ${formatGermanNumber(totalPerYear, 0)} € / Jahr`;
};

export const getBessPowerLabel = (data = {}) => {
  if (data.leistung_label) {
    return String(data.leistung_label);
  }

  const mwValue = parseGermanNumber(data.leistung_mw);
  if (mwValue > 0) {
    return `bis zu ${formatGermanNumber(mwValue)} MW`;
  }

  const kwValue = parseGermanNumber(data.leistung_kw);
  if (kwValue > 0) {
    return `bis zu ${formatGermanNumber(kwValue / 1000)} MW`;
  }

  return 'bis zu 1 MW';
};

export const getSpeicherPowerLabel = (data = {}) => (
  isBessOffer(data) ? getBessPowerLabel(data) : `${data.leistung_kw || '250'} kW`
);

export const getStorageExtensionText = (data = {}) => {
  if (!isBessOffer(data)) {
    return `2 × 5 J. (${data.vl_prozent || '5'}% Ertragsbeteil.)`;
  }

  const extensionPayment = getBessExtensionPayment(data);
  if (extensionPayment) {
    return extensionPayment;
  }

  return 'gemäß Verlängerungsvereinbarung';
};

const kleinspeicherBenefits = [
  { icon: Battery, ac: '#E07000', title: 'Minimaler Flächenbedarf', desc: 'Die reine Stellfläche eines 125-kW-Speichers beträgt maximal 5 m². Zwei Einheiten mit 250 kW benötigen maximal 10 m² und passen auf jedes Gewerbegrundstück.' },
  { icon: Clock, ac: '#122A5C', title: 'Schnelle Umsetzung', desc: 'Von der Netzprüfung bis zur ersten Vergütung rechnen wir mit ca. 4 bis 8 Monaten. Keine klassische Baugenehmigung nötig – ein erheblicher Zeitvorteil.' },
  { icon: TrendingUp, ac: '#E07000', title: 'Garantierte Vergütung', desc: 'Sie erhalten eine vertraglich gesicherte Vergütung – entweder als Einmalzahlung oder als jährliche Zahlung über die gesamte Laufzeit von 20 Jahren.' },
  { icon: Maximize2, ac: '#122A5C', title: 'Skalierbar bis 1 MW', desc: 'Sollte das Netz vor Ort Kapazitäten zulassen, kann die Anlage bis auf 1 MW erweitert werden. Damit steigt auch Ihre Vergütung proportional.' },
  { icon: Shield, ac: '#E07000', title: 'Null Risiko & Null Kosten', desc: 'EN:POWER übernimmt Installation, Betrieb, Wartung und Versicherung. Für Sie entstehen keinerlei Kosten. Die vollständige Verantwortung liegt bei uns.' },
  { icon: Zap, ac: '#122A5C', title: 'Netzstabilisierung', desc: 'Ihr Speicher leistet einen aktiven Beitrag zur Stabilisierung des Stromnetzes durch Frequenzregulierung und Spitzenlastmanagement – und wird dafür vergütet.' },
];

const bessBenefits = [
  { icon: Battery, ac: '#E07000', title: 'Hohe Flächenwertschöpfung', desc: 'Großspeicher nutzen geeignete Stellflächen wirtschaftlich aus und schaffen planbare Einnahmen über die vereinbarte Laufzeit.' },
  { icon: Clock, ac: '#122A5C', title: 'Baurechtliche Entwicklung', desc: 'Solarea verantwortet die baurechtliche Entwicklung und bringt Erfahrung aus Genehmigungspraxis sowie Zusammenarbeit mit großen Fonds ein.' },
  { icon: TrendingUp, ac: '#E07000', title: 'Garantierte Vergütung', desc: 'Sie erhalten eine vertraglich gesicherte Vergütung – entweder als Einmalzahlung oder als jährliche Zahlung über die gesamte Laufzeit.' },
  { icon: Maximize2, ac: '#122A5C', title: 'Netzplanung & Investoren', desc: 'EN:POWER übernimmt Akquise, netztechnische Planung und den Verkauf an IAB-Investoren. Die Auslegung folgt der verfügbaren Netzkapazität.' },
  { icon: Shield, ac: '#E07000', title: 'Null Risiko & Null Kosten', desc: 'EN:POWER und Solarea bündeln ihre Rollen in Akquise, Netz, Baurecht und Vermarktung. Für Sie entstehen keine Investitionskosten.' },
  { icon: Zap, ac: '#122A5C', title: 'Netzstabilisierung', desc: 'Der Großspeicher leistet einen aktiven Beitrag zur Stabilisierung des Stromnetzes durch Netzdienstleistungen und flexible Speicherleistung.' },
];

export const getSpeicherVariantCopy = (data = {}) => {
  if (isBessOffer(data)) {
    const powerLabel = getBessPowerLabel(data);

    return {
      footer: 'Gemeinschaftsprojekt EN:POWER × Solarea • BESS-Großspeicher',
      overline: 'Gemeinschaftsprojekt EN:POWER × Solarea',
      headline: 'GROSSSPEICHER FÜR IHR GRUNDSTÜCK',
      image: BESS_IMG,
      imageAlt: 'BESS-Großspeicher',
      heroImage: BESS_IMG,
      intro1: `Dieses Großspeicherprojekt ist ein Gemeinschaftsprojekt von ${BESS_PARTNER_LABEL}. Wir prüfen für Ihren Standort eine Leistung von ${powerLabel} und bewerten technische, netzseitige und baurechtliche Umsetzbarkeit.`,
      intro2: 'EN:POWER übernimmt Akquise, netztechnische Planung und den Verkauf an IAB-Investoren. Solarea verantwortet die baurechtliche Entwicklung sowie den Verkauf an Großinvestoren und Fonds und bringt dafür langjährige Erfahrung aus der Zusammenarbeit mit großen Fonds ein.',
      facts: [
        { l: 'Projektpartner', v: BESS_PARTNER_LABEL },
        { l: 'Geplante Leistung', v: powerLabel },
        { l: 'Laufzeit', v: '20 Jahre + 2 × 5 Jahre Verlängerungsoption' },
        { l: 'Rollen', v: 'EN:POWER: Akquise & Netzplanung; Solarea: Baurecht & Fondsvertrieb' },
      ],
      imageCaption: 'BESS-Großspeicher – Gemeinschaftsprojekt von EN:POWER und Solarea.',
      stats: [
        { value: powerLabel, label: 'Leistung', sub: 'Abhängig von Netzkapazität' },
        { value: data.stellflaeche || '~80', label: 'm² Stellfläche', sub: 'Indikative Fläche' },
        { value: 'Ja', label: 'Baugenehmigung', sub: 'Vor dem Bau erforderlich' },
        { value: '0€', label: 'Kosten für Sie', sub: 'Komplett kostenfrei' },
      ],
      benefits: bessBenefits,
    };
  }

  return {
    footer: 'Schwarmspeicher • Energiespeicher • Netzdienstleistung',
    overline: 'Schwarmspeicher',
    headline: 'KOMPAKTE ENERGIESPEICHER FÜR IHR GRUNDSTÜCK',
    image: UNIT_IMG,
    imageAlt: 'AlphaESS Speichereinheit',
    intro1: `Das EN:POWER Schwarmspeichersystem zeichnet sich durch seine minimale Stellfläche und schnelle Umsetzung aus. Wir planen standardmäßig mit zwei Einheiten à 125 kW (gesamt ${data.leistung_kw || '250'} kW), um die Wirtschaftlichkeit zu optimieren und die Netzprüfung realistisch zu halten.`,
    intro2: 'Die kompakten AlphaESS-Speichereinheiten werden auf Ihrem Grundstück aufgestellt und tragen durch Netzdienstleistungen zur Stabilisierung des Stromnetzes bei – und Sie profitieren finanziell.',
    facts: [
      { l: 'Stellfläche pro Einheit', v: 'max. 5 m² reine Stellfläche' },
      { l: 'Standard-Konfiguration', v: `${data.einheiten || '2 x 125'} kW = ${data.leistung_kw || '250'} kW` },
      { l: 'Maximale Skalierung', v: 'Bis 1 MW bei Netzkapazität' },
      { l: 'Zeitrahmen', v: '4–8 Monate bis erste Vergütung' },
    ],
    imageCaption: 'AlphaESS Kompaktspeicher – 125 kW pro Einheit, sofort einsatzbereit.',
    stats: [
      { value: data.leistung_kw || '250', label: 'kW Leistung', sub: `Standard (${data.einheiten || '2 x 125'} kW)` },
      { value: data.stellflaeche || '10', label: 'm² Stellfläche', sub: 'Reine Stellfläche' },
      { value: '4–8', label: 'Monate Umsetzung', sub: 'Bis erste Vergütung' },
      { value: '0€', label: 'Kosten für Sie', sub: 'Komplett kostenfrei' },
    ],
    benefits: kleinspeicherBenefits,
  };
};

export const PageFooterSpeicher = ({ page, variant }) => (
  <div className="page-footer">
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: variant === 'bess' ? '132px' : 'auto' }}>
      <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="8px" fontWeight={700} letterSpacing="0.04em" />
      {variant === 'bess' && (
        <>
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#CBD5E1', fontWeight: 700 }}>&times;</span>
          <img src={SOLAREA_LOGO_SRC} alt="Solarea Logo" style={{ width: '58px', height: '15px', objectFit: 'contain', display: 'block' }} />
        </>
      )}
    </span>
    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#CBD5E1', letterSpacing: '0.02em' }}>{getSpeicherVariantCopy({ offerVariant: variant }).footer}</span>
    <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '8.5px', fontWeight: 600, color: '#CBD5E1' }}>{page}</span>
  </div>
);

export const ContentPage2Speicher = ({ data = {}, editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange }) => {
  const offerData = {
    leistung_kw: '250',
    einheiten: '2 x 125',
    stellflaeche: '10',
    ...data,
  };
  const variantCopy = getSpeicherVariantCopy(offerData);
  const dragProps = {
    editMode,
    showMarkers,
    selectedElements,
    elementPositions,
    onSelectElement,
    onPositionChange,
    dragAxis: 'y',
  };

  return (
  <div className="a4-page" data-testid="page-2-sp" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <DraggableElement id="SP-S2-Heading" {...dragProps}>
    <div style={{ padding: '32px 48px 18px', background: 'white', flexShrink: 0 }}>
      <span className="section-overline">
        <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="9px" fontWeight={700} letterSpacing="0.16em" /> &bull; {variantCopy.overline}
      </span>
      <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: '0' }}>{variantCopy.headline}</h2>
      <div className="orange-rule" style={{ marginBottom: '0' }} />
    </div>
    </DraggableElement>

    {/* Intro + Product image */}
    <DraggableElement id="SP-S2-Intro" {...dragProps}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '18px', padding: '0 48px 20px', background: 'white', flexShrink: 0, minHeight: '200px', boxSizing: 'border-box' }}>
      <DraggableElement id="SP-S2-Intro-Text" {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <p className="proposal-body" style={{ fontSize: '10.5px', marginBottom: '9px' }}>
            {variantCopy.intro1}
          </p>
          <p className="proposal-body" style={{ fontSize: '10.5px' }}>
            {variantCopy.intro2}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px' }}>
          {variantCopy.facts.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#E07000', flexShrink: 0 }} />
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#475569', margin: 0 }}>
                <strong style={{ color: '#122A5C' }}>{r.l}:</strong> {r.v}
              </p>
            </div>
          ))}
        </div>
      </DraggableElement>

      {/* Product image card */}
      <DraggableElement id="SP-S2-Intro-Image" {...dragProps}>
      <div style={{ background: '#0E1E3C', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <img src={variantCopy.image} alt={variantCopy.imageAlt} style={{ width: '100%', height: '120px', objectFit: isBessOffer(offerData) ? 'cover' : 'contain', borderRadius: '6px', marginBottom: '8px' }} />
        <div>
          <div style={{ width: '28px', height: '2px', background: '#E07000', borderRadius: '1px', marginBottom: '6px' }} />
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
            {variantCopy.imageCaption}
          </p>
        </div>
      </div>
      </DraggableElement>
    </div>
    </DraggableElement>

    {/* Stats strip */}
    <DraggableElement id="SP-S2-Stats" {...dragProps}>
    <div data-no-override style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', flexShrink: 0, minHeight: '128px', padding: '20px 0', boxSizing: 'border-box' }}>
      {variantCopy.stats.map((s, i) => (
        <DraggableElement key={i} id={`SP-S2-Stat-${i + 1}`} {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '0 8px', height: '100%', borderRight: i < 3 ? '1px solid #E2E8F0' : 'none' }}>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', margin: '0 0 2px' }}>{s.value}</p>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1px' }}>{s.label}</p>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#94A3B8', margin: 0 }}>{s.sub}</p>
        </DraggableElement>
      ))}
    </div>
    </DraggableElement>

    <div style={{ height: '1px', background: '#E2E8F0', flexShrink: 0 }} />

    <DraggableElement id="SP-S2-Benefits-Heading" {...dragProps}>
    <div style={{ padding: '28px 48px 8px', background: 'white', flexShrink: 0 }}>
      <span className="section-overline">Was Sie gewinnen</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: '0' }}>IHRE VORTEILE AUF EINEN BLICK</h2>
      <div className="orange-rule" style={{ marginBottom: '0' }} />
    </div>
    </DraggableElement>

    <DraggableElement id="SP-S2-Benefits" {...dragProps}>
    <div style={{ padding: '40px 48px 40px', background: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'auto auto', gap: '20px', width: '100%' }}>
        {variantCopy.benefits.map((b, i) => {
          const Icon = b.icon;
          return (
            <DraggableElement key={i} id={`SP-S2-Benefit-${i + 1}`} {...dragProps}>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px 18px', background: '#FAFBFC', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: '245px', maxHeight: '245px' }}>
              <div style={{ width: '56px', height: '56px', background: b.ac, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', flexShrink: 0 }}>
                <Icon size={26} color="white" strokeWidth={2} />
              </div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', fontWeight: 700, color: '#122A5C', marginBottom: '10px', lineHeight: 1.3, flexShrink: 0 }}>{b.title}</h3>
              <p className="proposal-body" style={{ fontSize: '9.5px', lineHeight: 1.65, color: '#64748B', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical' }}>{b.desc}</p>
            </div>
            </DraggableElement>
          );
        })}
      </div>
    </div>
    </DraggableElement>

    <PageFooterSpeicher page={2} variant={isBessOffer(offerData) ? 'bess' : offerData.offerVariant} />
  </div>
  );
};
